import { useState } from 'react'
import { parseAsInteger, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { Button } from '@/components/ui/button'
import { SearchSelect } from '@/components/search-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Plus, Search } from 'lucide-react'
import { useProjects } from '@/api/hooks'
import { useMyProjectsFilter } from '@/hooks/use-my-projects-filter'
import { ProjectsTable, ProjectsTableSkeleton } from '@/components/projects-table'
import { DropshipQuoteUploadDialog } from '@/components/dropship-quote-upload-dialog'
import { StaffFilter } from '@/components/staff-filter'
import { ProjectPriorityFilter } from '@/components/project-priority-filter'
import { ServiceTierFilter } from '@/components/service-tier'
import { ListFilter, ListFilters } from '@/components/list-filters'
import { resultCount } from '@/lib/utils'
import { PROJECT_PRIORITY_FILTER_OPTIONS, projectPriorityParam } from '@/lib/project-priority'
import { SERVICE_TIER_FILTER_OPTIONS, serviceTierParam } from '@/lib/service-tier'
import { stageLabel } from '@/features/home/model'
import { SearchInput } from '@/components/search-input'

// The words on each project's status pill, which come from its POs (USA-1451).
const STAGE_OPTIONS = [
  'ordering',
  'po_review',
  'processing',
  'backordered',
  'on_hold',
  'needs_attention',
  'part_shipped',
  'in_transit',
  'part_delivered',
  'delivered',
  'cancelled',
]

const AUTOMATION_OPTIONS = ['all', 'enabled', 'disabled'] as const
const SOURCE_OPTIONS = ['all', 'shopify', 'dropship'] as const

export function ProjectsPage() {
  const [search, setSearch] = useUrlSearchText()
  const [filters, setFilters] = useQueryStates({
    status: parseAsString.withDefault('active'),
    stage: parseAsString,
    automation: parseAsStringLiteral(AUTOMATION_OPTIONS).withDefault('all'),
    source: parseAsStringLiteral(SOURCE_OPTIONS).withDefault('all'),
    priority: parseAsStringLiteral(PROJECT_PRIORITY_FILTER_OPTIONS).withDefault('all'),
    tier: parseAsStringLiteral(SERVICE_TIER_FILTER_OPTIONS).withDefault('all'),
    staff: parseAsInteger,
    page: parseAsInteger.withDefault(1),
  })
  const { status, stage, automation, source: sourceType, priority, tier, page } = filters
  const selectedStaffId = filters.staff ?? undefined
  const [dialogOpen, setDialogOpen] = useState(false)

  const { showMyProjects, staffId } = useMyProjectsFilter()
  const { data, isLoading } = useProjects({
    search: search || undefined,
    status: status === 'all' ? undefined : status,
    stage: stage ?? undefined,
    tracking_enabled: automation === 'all' ? undefined : automation === 'enabled',
    source_type: sourceType === 'all' ? undefined : sourceType,
    priority: projectPriorityParam(priority),
    service_tier: serviceTierParam(tier),
    staff_id: staffId ?? selectedStaffId,
    page,
    limit: 20,
  })

  // Filters set away from their defaults, counted on the Filters button.
  const activeFilters = [
    (stage ?? status) !== 'active',
    automation !== 'all',
    sourceType !== 'all',
    priority !== 'all',
    tier !== 'all',
    !showMyProjects && selectedStaffId != null,
  ].filter(Boolean).length

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold tracking-tight">Projects</h1>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Project
        </Button>
        <DropshipQuoteUploadDialog open={dialogOpen} onOpenChange={setDialogOpen} />
      </div>

      <ListFilters

        count={resultCount(data?.total, 'project')}
        active={activeFilters}
        onClear={() =>
          void setFilters({ status: null, stage: null, automation: null, source: null, priority: null, tier: null, staff: null, page: 1 })
        }
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <SearchInput
              placeholder="Search projects..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                void setFilters({ page: 1 })
              }}
              className="pl-9"
            />
          </div>
        }
      >
        <ListFilter label="Status">
        <SearchSelect
          ariaLabel="Project status filter"
          className="h-9 w-full"
          value={stage ?? status}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'all', label: 'All Statuses' },
            ...STAGE_OPTIONS.map((key) => ({ value: key, label: stageLabel(key) })),
          ]}
          onChange={(v) => {
            if (!v) return
            // A pill word filters every project; Active is the open ones, as before.
            void setFilters(
              STAGE_OPTIONS.includes(v) ? { stage: v, status: 'all', page: 1 } : { status: v, stage: null, page: 1 },
            )
          }}
          placeholder="Status"
          searchPlaceholder="Search statuses"
        />
        </ListFilter>
        <ListFilter label="Automation">
        <Select
          value={automation}
          onValueChange={(v) => {
            void setFilters({ automation: v as (typeof AUTOMATION_OPTIONS)[number], page: 1 })
          }}
        >
          <SelectTrigger aria-label="Project automation filter" className="w-full">
            <SelectValue placeholder="Automation" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="enabled">Automation On</SelectItem>
            <SelectItem value="disabled">Automation Off</SelectItem>
            <SelectItem value="all">All Projects</SelectItem>
          </SelectContent>
        </Select>
        </ListFilter>
        <ListFilter label="Source">
        <Select
          value={sourceType}
          onValueChange={(v) => {
            void setFilters({ source: v as (typeof SOURCE_OPTIONS)[number], page: 1 })
          }}
        >
          <SelectTrigger aria-label="Project source filter" className="w-full">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="shopify">Shopify</SelectItem>
            <SelectItem value="dropship">Internal</SelectItem>
          </SelectContent>
        </Select>
        </ListFilter>
        <ListFilter label="Priority">
        <ProjectPriorityFilter
          value={priority}
          onValueChange={(value) => void setFilters({ priority: value, page: 1 })}
        />
        </ListFilter>
        <ListFilter label="Service tier">
          <ServiceTierFilter value={tier} onValueChange={(value) => void setFilters({ tier: value, page: 1 })} />
        </ListFilter>
        <ListFilter label="Staff">
        <StaffFilter
          value={showMyProjects ? staffId : selectedStaffId}
          disabled={showMyProjects}
          onValueChange={(value) => {
            void setFilters({ staff: value ?? null, page: 1 })
          }}
        />
        </ListFilter>
      </ListFilters>

      {isLoading ? (
        <ProjectsTableSkeleton />
      ) : data?.items?.length ? (
        <ProjectsTable items={data.items} />
      ) : (
        <div className="text-center text-muted-foreground py-12">No projects found</div>
      )}

      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-sm text-muted-foreground">
          Page {data?.page ?? 1} of {data?.pages ?? 1} ({data?.total ?? 0} total)
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setFilters({ page: Math.max(1, page - 1) })}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setFilters({ page: page + 1 })}
            disabled={page >= (data?.pages ?? 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
